/**
 * Aufgezeichnete Schreibvorgaenge verdichten, auf das Noetige kuerzen und
 * in Stuecke teilen (F-45). Rein und ohne Dexie.
 */
import { gleich, type Daten } from '@/domain/sync/zusammenfuehren';
import { EXKLUSIV, ZEILE_HART_MAX_BYTES, ZEILE_MAX_BYTES, type Aenderung, type Zaehler } from '@/domain/papierkorb/typen';

/** Ein einzelner Schreibvorgang, so wie die Aufzeichnung ihn sieht. */
export interface RohAenderung {
  t: string;
  id: string;
  vorher?: Daten;
  nachher?: Daten;
}

/** Verwaltungsfeld - zaehlt nie als inhaltliche Aenderung. */
const VERWALTUNG = 'updatedAt';

export function ohneFelder(d: Daten, felder: readonly string[]): Daten {
  const kopie = { ...d };
  for (const f of felder) delete kopie[f];
  return kopie;
}

/** Gleich bis auf `updatedAt` (und weitere genannte Felder)? */
export function inhaltlichGleich(a: Daten | undefined, b: Daten | undefined, auch: readonly string[] = []): boolean {
  if (!a || !b) return a === b;
  const felder = [VERWALTUNG, ...auch];
  return gleich(ohneFelder(a, felder), ohneFelder(b, felder));
}

/**
 * Je Zeile eine Aenderung: erstes `vorher`, letztes `nachher`. Angelegt und
 * im selben Zug wieder geloescht faellt weg, ebenso Schreibvorgaenge, die
 * nur `updatedAt` aendern. Die Reihenfolge folgt dem ersten Auftreten.
 */
export function verdichten(roh: readonly RohAenderung[]): Aenderung[] {
  const nachSchluessel = new Map<string, Aenderung>();
  for (const r of roh) {
    const k = `${r.t}/${r.id}`;
    const da = nachSchluessel.get(k);
    if (!da) {
      const neu: Aenderung = { t: r.t, id: r.id };
      if (r.vorher) neu.vorher = r.vorher;
      if (r.nachher) neu.nachher = r.nachher;
      nachSchluessel.set(k, neu);
      continue;
    }
    if (r.nachher) da.nachher = r.nachher;
    else delete da.nachher;
  }
  return [...nachSchluessel.values()].filter((a) => (a.vorher || a.nachher) && !(a.vorher && a.nachher && inhaltlichGleich(a.vorher, a.nachher)));
}

const auswahl = (d: Daten, felder: Iterable<string>): Daten => {
  const out: Daten = {};
  for (const f of felder) if (d[f] !== undefined) out[f] = d[f];
  return out;
};

/**
 * Bei einer geaenderten Zeile nur die geaenderten obersten Felder behalten
 * (`teil: 1`, ohne `updatedAt`). Gehoert ein Feld zu einer EXKLUSIV-Gruppe,
 * kommt die ganze Gruppe mit - sonst laesst sich spaeter nicht erkennen, ob
 * die Zeile inzwischen woanders haengt. Angelegte und geloeschte Zeilen
 * bleiben vollstaendig.
 */
export function alsDiff(a: Aenderung): Aenderung {
  if (!a.vorher || !a.nachher) return a;
  const { vorher, nachher } = a;
  const felder = new Set(
    [...new Set([...Object.keys(vorher), ...Object.keys(nachher)])]
      .filter((k) => k !== VERWALTUNG && !gleich(vorher[k], nachher[k])),
  );
  for (const gruppe of EXKLUSIV[a.t] ?? []) {
    if (gruppe.some((f) => felder.has(f))) for (const f of gruppe) felder.add(f);
  }
  const out: Aenderung = { t: a.t, id: a.id, vorher: auswahl(vorher, felder), nachher: auswahl(nachher, felder), teil: 1 };
  if (a.datei) out.datei = 1;
  return out;
}

const encoder = new TextEncoder();
export const utf8Bytes = (wert: unknown): number => encoder.encode(JSON.stringify(wert) ?? '').length;

export interface Stueck {
  schluessel: string[];
  aenderungen: Aenderung[];
}

/**
 * Nach UTF-8-Groesse in Stuecke <= `max` teilen (Aenderung plus Schluessel).
 * Eine einzelne groessere Aenderung bekommt ein eigenes Stueck - das ist
 * sicher, weil die Originalzeile selbst schon unter dem Server-Limit lag.
 */
export function zerlegen(aenderungen: readonly Aenderung[], max = ZEILE_MAX_BYTES): Stueck[] {
  const stuecke: Stueck[] = [];
  let aktuell: Stueck = { schluessel: [], aenderungen: [] };
  let groesse = 0;
  for (const a of aenderungen) {
    const schluessel = `${a.t}/${a.id}`;
    const n = utf8Bytes(a) + utf8Bytes(schluessel) + 2;
    if (aktuell.aenderungen.length && groesse + n > max) {
      stuecke.push(aktuell);
      aktuell = { schluessel: [], aenderungen: [] };
      groesse = 0;
    }
    aktuell.schluessel.push(schluessel);
    aktuell.aenderungen.push(a);
    groesse += n;
  }
  if (aktuell.aenderungen.length) stuecke.push(aktuell);
  return stuecke;
}

/** Groesse eines Stuecks in UTF-8 (Aenderungen plus Schluessel). */
export const stueckBytes = (s: Stueck): number => utf8Bytes(s.aenderungen) + utf8Bytes(s.schluessel);

/** Liegt ein Stueck ueber der harten Grenze (siehe ZEILE_HART_MAX_BYTES)? */
export const zuGross = (stuecke: readonly Stueck[], hart = ZEILE_HART_MAX_BYTES): boolean =>
  stuecke.some((s) => stueckBytes(s) > hart);

/** Je Tabelle: geloeschte, geaenderte und angelegte Zeilen. */
export function zaehlen(aenderungen: readonly Aenderung[]): Record<string, Zaehler> {
  const z: Record<string, Zaehler> = {};
  for (const a of aenderungen) {
    const e = (z[a.t] ??= { weg: 0, geaendert: 0, neu: 0 });
    if (a.vorher && a.nachher) e.geaendert++;
    else if (a.vorher) e.weg++;
    else e.neu++;
  }
  return z;
}
